/* eslint-disable @typescript-eslint/naming-convention */
import { SearchFilterOption } from "../../../shared/components/search-bar/search-filter/search-filter.component";
import { SearchInputOption } from "../../../shared/components/search-bar/search-input/search-input.component";
import { yesNoOptions } from "../../../shared/constants/components";
import { UserInfo } from "../../../shared/types/user-info";
import { CompanyService } from "../companies/company.service";
import { PartnerService } from "../partners/partner.service";
import { Ticket } from "./ticket.type";

//#region Search Filter Options

export const getTicketSearchFilterOptions = (
  user: UserInfo,
  config?: {
    shown?: string[];
    hidden?: string[];
    companyService?: CompanyService;
    partnerService?: PartnerService;
  },
): SearchFilterOption<Ticket>[] => {
  const { shown, hidden, companyService, partnerService } = config || {};
  return (
    [
      { value: "zendeskId", label: "ID" },
      {
        value: "subject",
        label: "Subject",
      },
      {
        value: "priority",
        label: "Priority",
        inputType: "dropdown",
        condition: "$eq",
        options: [...ticketPriorities],
      },
      {
        value: "description",
        label: "Description",
      },
      {
        value: "status",
        label: "Status",
        inputType: "dropdown",
        condition: "$eq",
        options: [...ticketStatuses],
      },
      {
        value: "requesterEmail",
        label: "Requester",
      },
      {
        value: "asset.company.name",
        auxValue: "companyId",
        label: "Company",
        hidden: user.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }) || !companyService,
        filterByProperty: { asset: { company: { name: true } } },
      },
      {
        value: "asset.company.partner.name",
        auxValue: "partnerId",
        label: "Partner",
        hidden: user.isPartnerOrViewAsPartner() || user.isCompanyOrViewAsCompany() || !partnerService,
        filterByProperty: { asset: { company: { partner: { name: true } } } },
      },
      {
        value: "serialNumber",
        label: "Serial #",
        filterByProperty: { asset: { serialNumber: true } },
      },
      {
        value: "failureType",
        label: "Failure Type",
        inputType: "dropdown",
        condition: "$eq",
        options: [...ticketFailureTypes],
      },
      {
        value: "contract.number",
        label: "Contract #",
        filterByFunction: (value: string) => {
          const [number, version] = value?.toString().split("-") || [];
          return {
            contract: { ...(number && { number: { $cont: number } }), ...(version && { version: { $cont: version } }) },
          };
        },
      },
      {
        value: "onboarding",
        label: "Onboarding",
        inputType: "dropdown",
        hidden: !user.isGlobal(),
        condition: "$eq",
        options: [...yesNoOptions],
      },
    ] as SearchFilterOption<Ticket>[]
  ).filter((opt) => (shown ? shown.includes(opt.value) : true) && (hidden ? !hidden.includes(opt.value) : true));
};

//#endregion

//#region Search Input Options

export const ticketPriorities: SearchInputOption[] = [
  { label: "Low", value: "low" },
  { label: "Normal", value: "normal" },
  { label: "High", value: "high" },
  { label: "Urgent", value: "urgent" },
];

export const ticketFailureTypes: SearchInputOption[] = [
  { label: "Cpu", value: "cpu" },
  { label: "Drive", value: "drive" },
  { label: "Fan", value: "fan" },
  { label: "Power Supply", value: "power_supply" },
  { label: "System Board", value: "system_board" },
  { label: "Controller", value: "controller" },
  { label: "Memory", value: "memory", hidden: true },
  { label: "Other", value: "other" },
  { label: "Memory Board", value: "memory_board" },
  { label: "Memory DIMM", value: "memory_dimm" },
  { label: "Processor Board", value: "processor_board" },
  { label: "Daughter Board", value: "daughter_board" },
  { label: "Riser Board", value: "riser_board" },
  { label: "Storage", value: "storage" },
  { label: "Storage ESM/LCC", value: "storage_esm_lcc" },
  { label: "PCI", value: "pci" },
  { label: "Battery", value: "battery" },
  { label: "Chassis", value: "chassis" },
  { label: "Tape Drive", value: "tape_drive" },
];

export const ticketStatuses: SearchInputOption[] = [
  { label: "New", value: "new" },
  { label: "Open", value: "open" },
  { label: "Pending", value: "pending" },
  { label: "Solved", value: "solved" },
  { label: "Closed", value: "closed" },
];

//#endregion
